import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <tour-detail> custom element used by the template.
import "../../components/tour/detail";

import template from "./TourListTemplate.html";
import { _lcs } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// The labels and titles below are German source strings that double as
// dictionary keys — module constants are evaluated before I18n exists, so the
// translation happens where they are read.
const STATUS_LABELS = {
    published : "Veröffentlicht",
    draft     : "Entwurf",
    archived  : "Archiviert"
};

/**
 * Activity vocabulary (core-api: Tour.ACTIVITY_TYPE), ordered the way the
 * pool is distributed — hiking and cycling are three quarters of it.
 */
const ACTIVITIES = [
    { key: "hiking",                title: "Wandern",       icon: "footprints" },
    { key: "walking",               title: "Spazieren",     icon: "footprints" },
    { key: "cycling",               title: "Radfahren",     icon: "bike" },
    { key: "road_cycling",          title: "Rennrad",       icon: "bike" },
    { key: "mountain_biking",       title: "Mountainbike",  icon: "bike" },
    { key: "pilgrimage",            title: "Pilgerweg",     icon: "compass" },
    { key: "paddling",              title: "Kanu",          icon: "ship" },
    { key: "horse_riding",          title: "Reiten",        icon: "paw-print" },
    { key: "running",               title: "Laufen",        icon: "footprints" },
    { key: "climbing",              title: "Klettern",      icon: "mountain" },
    { key: "winter_hiking",         title: "Winterwandern", icon: "snowflake" },
    { key: "snowshoe",              title: "Schneeschuh",   icon: "snowflake" },
    { key: "cross_country_skiing",  title: "Langlauf",      icon: "snowflake" },
    { key: "sightseeing",           title: "Stadtrundgang", icon: "landmark" },
    { key: "other",                 title: "Sonstiges",     icon: "route" }
];

const ACTIVITY_TITLES = ACTIVITIES.reduce( ( map, a ) => { map[ a.key ] = a.title; return map; }, {} );

const DIFFICULTY_LABELS = {
    unknown   : "—",
    easy      : "Leicht",
    moderate  : "Mittel",
    difficult : "Schwer",
    expert    : "Sehr schwer"
};

const ROUTE_TYPE_LABELS = {
    loop           : "Rundweg",
    point_to_point : "Strecke",
    out_and_back   : "Hin und zurück"
};

/**
 * TourList — "Touren": the platform's own pool of routes
 * (GET /tour/platform).
 *
 * Same shape as the Gastronomie and Sehenswürdigkeiten screens. What differs
 * is what a route is picked by: the table sorts on distance, ascent and
 * duration rather than on a category, and the filters are activity,
 * difficulty and "how far am I willing to walk".
 */
export class TourList extends InAppState
{
    static ROUTE = '/tours';
    static ID = 'tour-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="tours-state"]' );
        this.truncatedEl = root.querySelector( '[data-id="truncated"]' );
        this.sourceFilter = root.querySelector( '[data-id="filter-source"]' );
        this.activityFilter = root.querySelector( '[data-id="filter-activity"]' );
        this.difficultyFilter = root.querySelector( '[data-id="filter-difficulty"]' );
        this.distanceFilter = root.querySelector( '[data-id="filter-distance"]' );
        this.statusFilter = root.querySelector( '[data-id="filter-status"]' );
        this.duplicateFilter = root.querySelector( '[data-id="filter-duplicates"]' );
        this.detail = root.querySelector( '[data-id="tour-detail"]' );

        this.tours = [];
        this.tourById = new Map();
        this.sources = [];
        this.grid = null;

        // Handed over by the source list ("Touren" on a source card).
        this.selectedSourceId = this.app.pendingTourSourceId || "";
        this.app.pendingTourSourceId = null;

        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );
        this.addEventListener( this.detail, "tour-saved", () => this.loadTours() );
        [ this.sourceFilter, this.activityFilter, this.difficultyFilter,
          this.distanceFilter, this.statusFilter, this.duplicateFilter ].forEach( ( el ) =>
            this.addEventListener( el, "change", () => this.loadTours() ) );
        this.addEventListener( root.querySelector( '[data-id="btn-reload"]' ), "click", () => this.loadTours() );

        this.renderActivityFilter();

        await this.loadSources();
        await this.loadTours();
    }

    renderActivityFilter()
    {
        this.activityFilter.innerHTML =
            '<option value="">' + this._escape( _lcs( "Alle Aktivitäten" ) ) + '</option>' +
            ACTIVITIES.map( ( a ) =>
                '<option value="' + this._escape( a.key ) + '">' + this._escape( _lcs( a.title ) ) + '</option>'
            ).join( "" );
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadSources()
    {
        try
        {
            const { result } = await this.app.api.get( "/eventsource" );
            const all = ( result && +result.status === 200 && Array.isArray( result.data ) ) ? result.data : [];
            this.sources = all.filter( ( source ) => source.contentType === "tour" );
        }
        catch ( e )
        {
            this.sources = [];
        }

        this.sourceFilter.innerHTML = '<option value="">' + this._escape( _lcs( "Alle Quellen" ) ) + '</option>' +
            this.sources.map( ( source ) =>
                '<option value="' + this._escape( source.id ) + '">' + this._escape( source.name || source.key ) + '</option>'
            ).join( "" );

        this.sourceFilter.value = this.selectedSourceId || "";
    }

    async loadTours()
    {
        this.showMessage( "route", _lcs( "Lade Touren …" ), _lcs( "Einen Moment bitte." ) );
        this.truncatedEl.hidden = true;

        const query = [];
        if ( this.sourceFilter.value ) query.push( "source=" + encodeURIComponent( this.sourceFilter.value ) );
        if ( this.activityFilter.value ) query.push( "activity=" + encodeURIComponent( this.activityFilter.value ) );
        if ( this.difficultyFilter.value ) query.push( "difficulty=" + encodeURIComponent( this.difficultyFilter.value ) );
        if ( this.statusFilter.value ) query.push( "status=" + encodeURIComponent( this.statusFilter.value ) );
        if ( this.duplicateFilter.value !== "exclude" ) query.push( "duplicates=" + encodeURIComponent( this.duplicateFilter.value ) );

        let payload = null;
        try
        {
            const { result } = await this.app.api.get( "/tour/platform" + ( query.length ? "?" + query.join( "&" ) : "" ) );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected /tour/platform response." );
            }
            payload = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage( "triangle-alert",
                _lcs( "Touren konnten nicht geladen werden" ), _lcs( "Bitte versuche es später erneut." ) );
            return;
        }

        this.tours = Array.isArray( payload.items ) ? payload.items : [];

        // The distance filter is applied here rather than in the query: the
        // API takes maxDistance only on the guest route, and the admin list is
        // already in hand.
        const maxDistance = +this.distanceFilter.value;
        if ( Number.isFinite( maxDistance ) && maxDistance > 0 )
        {
            this.tours = this.tours.filter( ( tour ) =>
                tour.distanceMeters === null || tour.distanceMeters <= maxDistance );
        }

        this.tourById = new Map();
        this.tours.forEach( ( tour ) => this.tourById.set( String( tour.id ), tour ) );

        if ( payload.isTruncated )
        {
            this.truncatedEl.textContent = _lcs(
                "Es werden die ersten {limit} Touren angezeigt. Grenze die Auswahl über die Filter weiter ein.",
                { limit: payload.limit }
            );
            this.truncatedEl.hidden = false;
        }

        if ( this.tours.length === 0 )
        {
            this.showMessage(
                "route",
                _lcs( "Keine Touren gefunden" ),
                _lcs( "Für diese Auswahl liegen keine Daten vor. Der Import läuft über „Datenquellen“." )
            );
            return;
        }

        this.renderGrid();
    }

    renderGrid()
    {
        this._destroyGrid();
        this.stateEl.innerHTML = "";

        const rows = this.tours.map( ( tour ) => [
            tour.name || "—",
            ACTIVITY_TITLES[ tour.activityType ]
                ? _lcs( ACTIVITY_TITLES[ tour.activityType ] ) : ( tour.activityType || "—" ),
            tour.distanceKm !== null && tour.distanceKm !== undefined ? tour.distanceKm : -1,
            tour.ascentMeters !== null && tour.ascentMeters !== undefined ? tour.ascentMeters : -1,
            tour.durationMinutes !== null && tour.durationMinutes !== undefined ? tour.durationMinutes : -1,
            // "unknown" maps to an em dash, which needs no translating.
            DIFFICULTY_LABELS[ tour.difficulty ]
                ? _lcs( DIFFICULTY_LABELS[ tour.difficulty ] ) : ( tour.difficulty || "—" ),
            String( tour.status || "" ),
            String( tour.id )
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Name" ),
                    formatter: ( cell, row ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._markersHtml( row.cells[ 7 ] ? this.tourById.get( String( row.cells[ 7 ].data ) ) : null ) +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                _lcs( "Aktivität" ),
                {
                    // Sorted numerically, rendered with its unit. -1 stands for
                    // "not measured" so it sorts to one end instead of
                    // scattering through the list as text.
                    name: _lcs( "Distanz" ),
                    formatter: ( cell ) => html( cell < 0 ? "—" : this._escape( cell + " km" ) )
                },
                {
                    name: _lcs( "Aufstieg" ),
                    formatter: ( cell ) => html( cell < 0 ? "—" : this._escape( cell + " hm" ) )
                },
                {
                    name: _lcs( "Dauer" ),
                    formatter: ( cell ) => html( cell < 0 ? "—" : this._escape( this._durationText( cell ) ) )
                },
                _lcs( "Schwierigkeit" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html(
                        '<span class="acc-status acc-status--' + this._escape( String( cell ).toLowerCase() ) + '">' +
                            this._escape( STATUS_LABELS[ cell ] ? _lcs( STATUS_LABELS[ cell ] ) : ( cell || "—" ) ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) =>
                    {
                        const details = this._escape( _lcs( "Details" ) );
                        return html(
                            '<div class="admin-users__actions">' +
                                '<button type="button" class="admin-users__action" data-action="open" ' +
                                        'data-tour-id="' + this._escape( cell ) + '" title="' + details + '" aria-label="' + details + '">' +
                                    '<i class="icon icon--eye"></i><span>' + details + '</span>' +
                                '</button>' +
                            '</div>'
                        );
                    }
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 25, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Touren" ), _lcs( "Keine Touren gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    _markersHtml( tour )
    {
        if ( !tour )
        {
            return "";
        }

        let markers = "";
        if ( tour.isLocked )
        {
            markers += '<i class="icon icon--lock evp__lock" title="' +
                this._escape( _lcs( "Manuell bearbeitet — wird beim Import nicht überschrieben" ) ) + '"></i> ';
        }
        if ( tour.isDuplicate )
        {
            markers += '<i class="icon icon--layers evp__dupe" title="' +
                this._escape( _lcs( "Dublette — diese Tour existiert bereits und wird Gästen nicht angezeigt" ) ) + '"></i> ';
        }
        if ( !tour.hasTrack )
        {
            markers += '<i class="icon icon--triangle-alert plc__closed" title="' +
                this._escape( _lcs( "Kein Streckenverlauf hinterlegt" ) ) + '"></i> ';
        }
        return markers;
    }

    /* -------------------------------- Actions -------------------------------- */

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        if ( btn.getAttribute( "data-action" ) === "open" )
        {
            this.openDetail( btn.getAttribute( "data-tour-id" ) );
        }
    }

    /** The table holds compact rows; the track only comes with the detail. */
    async openDetail( tourId )
    {
        try
        {
            const { result } = await this.app.api.get( "/tour/platform/" + tourId );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected tour response." );
            }
            this.detail.open( result.data, this._sourceName( result.data.contentSourceId ) );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Tour konnte nicht geladen werden." ), "error" );
        }
    }

    /* ------------------------------- Formatting ------------------------------ */

    _durationText( minutes )
    {
        const total = Math.round( +minutes );
        if ( !Number.isFinite( total ) || total <= 0 )
        {
            return "—";
        }
        const hours = Math.floor( total / 60 );
        const rest = total % 60;
        return hours > 0 ? `${hours}:${String( rest ).padStart( 2, "0" )} h` : `${rest} min`;
    }

    _sourceName( contentSourceId )
    {
        const source = this.sources.find( ( s ) => String( s.id ) === String( contentSourceId ) );
        return source ? ( source.name || source.key ) : "—";
    }

    showMessage( icon, title, text )
    {
        this._destroyGrid();
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2><p></p>' +
            '</div>';
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _destroyGrid()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is replaced anyway */ }
            this.grid = null;
        }
    }

    async exit()
    {
        this._destroyGrid();
        await super.exit();
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Touren" ),
            subtitle: _lcs( "Wander-, Rad- und Themenwege der Region" )
        };
    }
}
